/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useState, useEffect, useRef } from 'react'
import { defineMessages, useIntl } from 'react-intl'

import type { CriteriaWithPagination } from '@elastic/eui'
import { EuiFlexGroup } from '@elastic/eui'

import { useDeleteCCMCluster, useGetCCMClusters } from '@modules/ccm-admin-lib/hooks'
import { addToast } from '@modules/cui/Toasts'
import type { AdminConsoleCCMCluster } from '@modules/ccm-admin-lib/types'

import ClusterTable from './ClusterTable'
import ConnectedClustersFilters from './ConnectedClustersFilters'
import useCCMClusterSearchFiltersFromQueryString from './hooks'

import type { FC } from 'react'
import type { CCMClusterTableColumns, FilterableCCMClusterProperties } from './types'

const messages = defineMessages({
  success: {
    id: 'ccm.clusters-table.success',
    defaultMessage: 'Success',
  },
  error: {
    id: 'ccm.clusters-table.error',
    defaultMessage: 'Error',
  },
  clusterFailedToDisconnect: {
    id: 'ccm.clusters-table.cluster-failed-to-disconnect',
    defaultMessage: 'Failed to disconnect cluster {clusterName}',
  },
  clusterDisconnected: {
    id: 'ccm.clusters-table.cluster-disconnected',
    defaultMessage: 'Cluster {clusterName} has been successfully disconnected',
  },
})

const DEFAULT_PAGE_SIZE = 20
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

type Props = {
  organizationId?: string
  enabledFilters?: FilterableCCMClusterProperties[]
  visibleColumns?: CCMClusterTableColumns[]
}

const FilterableConnectedClusterTable: FC<Props> = ({
  organizationId,
  enabledFilters,
  visibleColumns,
}) => {
  const { formatMessage } = useIntl()

  const { activeQueryStringFilters } = useCCMClusterSearchFiltersFromQueryString()

  // Server-side pagination state and next-page tokens
  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  // tokens[i] is the header token required to fetch page i (i > 0)
  const [tokens, setTokens] = useState<string[]>([])
  // Track which page was requested (used to map next_page token to the correct next index)
  const requestedPageRef = useRef(0)

  // Reset pagination when filters change
  useEffect(() => {
    setPageIndex(0)
    setTokens([])
    requestedPageRef.current = 0
  }, [
    organizationId,
    activeQueryStringFilters.selfManagedClusterId,
    activeQueryStringFilters.organizationId,
    activeQueryStringFilters.licenseId,
    activeQueryStringFilters.includeDisconnected,
    activeQueryStringFilters.includeInternalUsers,
  ])

  const { data, isLoading, isFetching } = useGetCCMClusters({
    queryParameters: {
      self_managed_cluster_id: activeQueryStringFilters.selfManagedClusterId,
      organization_id: organizationId ?? activeQueryStringFilters.organizationId,
      license_uid: activeQueryStringFilters.licenseId,
      include_deleted: activeQueryStringFilters.includeDisconnected,
      exclude_created_by_internal_user: !activeQueryStringFilters.includeInternalUsers,
    },
    headerParameters: {
      'X-Elastic-Page-Size': pageSize,
      // Only use the token we stored for the requested page.
      'X-Elastic-Next-Page': pageIndex === 0 || !tokens[pageIndex] ? undefined : tokens[pageIndex],
    },
    options: {
      keepPreviousData: true,
    },
  })

  // Persist the next page token returned by the server for the page we just fetched
  // If we fetched page `i` and server returns `next_page`, store it at tokens[i+1]
  useEffect(() => {
    const nextToken = typeof data?.next_page === 'string' ? data.next_page : undefined

    if (!nextToken) {
      return
    }

    const fetchedPage = requestedPageRef.current

    setTokens((prev) => {
      const next = prev.slice()

      // Avoid unnecessary state updates
      if (next[fetchedPage + 1] !== nextToken) {
        next[fetchedPage + 1] = nextToken
      }

      return next
    })
  }, [data?.next_page])

  const { mutate: deleteCCMCluster, isLoading: isDeleting } = useDeleteCCMCluster({
    onSuccess: (_, disconnectedCluster) => {
      addToast({
        title: formatMessage(messages.success),
        color: 'success',
        iconType: 'check',
        text: formatMessage(messages.clusterDisconnected, {
          clusterName: disconnectedCluster.id,
        }),
      })
    },
    onError: (_, payload) => {
      addToast({
        title: formatMessage(messages.error),
        color: 'danger',
        iconType: 'cross',
        text: formatMessage(messages.clusterFailedToDisconnect, {
          clusterName: payload.id,
        }),
      })
    },
  })

  const onClusterDisconnect = (clusterId: string) => {
    deleteCCMCluster({ id: clusterId })
  }

  const handlePageSizeChange = (size: number) => {
    setPageSize(size)
    setPageIndex(0)
    setTokens([])
    requestedPageRef.current = 0
  }

  const handlePageIndexChange = (index: number) => {
    requestedPageRef.current = index
    setPageIndex(index)
  }

  const onTableChange = ({ page }: CriteriaWithPagination<AdminConsoleCCMCluster>) => {
    const { index, size } = page

    if (size !== pageSize) {
      handlePageSizeChange(size)
      return
    }

    if (index !== pageIndex) {
      handlePageIndexChange(index)
    }
  }

  const items = data?.items ?? []
  const hasNext = Boolean(data?.next_page)
  const totalItemCount = hasNext
    ? (pageIndex + 2) * pageSize // show one extra page to enable next
    : pageIndex * pageSize + items.length

  return (
    <EuiFlexGroup direction='column' gutterSize='l'>
      <ConnectedClustersFilters enabledFilters={enabledFilters} />
      <ClusterTable
        clusters={items}
        isLoading={isLoading || isFetching || isDeleting}
        onClusterDisconnect={onClusterDisconnect}
        visibleColumns={visibleColumns}
        pagination={{
          pageIndex,
          pageSize,
          totalItemCount,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
        }}
        onTableChange={onTableChange}
      />
    </EuiFlexGroup>
  )
}

export default FilterableConnectedClusterTable
